/**
 * 登录页面（支持密码登录 + 扫码登录）
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Button, Card, message, Typography, Tabs, Spin, Result } from 'antd';
import { UserOutlined, LockOutlined, ToolOutlined, QrcodeOutlined, KeyOutlined, ReloadOutlined } from '@ant-design/icons';
import { authApi } from '../services/api';
import { useSystemConfig } from '../config/system';

const { Title, Text } = Typography;

const API_BASE = process.env.REACT_APP_API_URL || '/api';
const POLL_INTERVAL = 2000;

function Login({ onLogin }) {
  const [loading, setLoading] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [ssoError, setSsoError] = useState('');
  const [activeTab, setActiveTab] = useState('password');
  const [qrToken, setQrToken] = useState(null);
  const [qrStatus, setQrStatus] = useState('idle'); // idle | pending | scanned | confirmed | expired
  const [qrLoading, setQrLoading] = useState(false);
  const [scannerName, setScannerName] = useState('');
  const pollRef = useRef(null);
  const navigate = useNavigate();
  const sysConfig = useSystemConfig();

  // SSO 单点登录：检查 URL 参数中的 sso token
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ssoToken = params.get('sso');
    if (!ssoToken) return;

    setSsoLoading(true);
    fetch(`${API_BASE}/auth/sso`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: ssoToken }),
    })
      .then(res => res.json())
      .then(data => {
        if (data.code === 200 && data.data) {
          localStorage.setItem('admin_token', data.data.token);
          localStorage.setItem('admin_info', JSON.stringify(data.data.admin));
          onLogin?.(data.data.admin);
          message.success('已从主站同步登录');
          // 清除 URL 中的 sso 参数
          window.history.replaceState({}, '', window.location.pathname);
          setTimeout(() => navigate('/dashboard'), 300);
        } else {
          setSsoError(data.message || 'SSO 登录失败，请手动登录');
          setSsoLoading(false);
        }
      })
      .catch(() => {
        setSsoError('SSO 验证服务不可用，请手动登录');
        setSsoLoading(false);
      });
  }, [navigate, onLogin]);

  // 密码登录
  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const res = await authApi.login(values.username, values.password);
      localStorage.setItem('admin_token', res.data.token);
      localStorage.setItem('admin_info', JSON.stringify(res.data.admin));
      onLogin?.(res.data.admin);
      message.success('登录成功');
      navigate('/dashboard');
    } catch (err) {
      // 错误已在拦截器中处理，但如果是初始化提示，显示更明确
      const msg = err?.response?.data?.message || err?.message || '';
      if (msg && msg.includes('尚未初始化')) {
        message.warning(msg, 8);
      }
    } finally {
      setLoading(false);
    }
  };

  // 生成二维码
  const generateQrCode = useCallback(async () => {
    setQrLoading(true);
    setQrStatus('idle');
    setScannerName('');
    try {
      const resp = await fetch(`${API_BASE}/auth/qrcode/generate`);
      const data = await resp.json();
      if (data.code === 200) {
        setQrToken(data.data.token);
        setQrStatus('pending');
      } else {
        message.error(data.message || '生成二维码失败');
      }
    } catch {
      message.error('网络错误，无法生成二维码');
    } finally {
      setQrLoading(false);
    }
  }, []);

  // 轮询扫码状态
  useEffect(() => {
    if (!qrToken || qrStatus !== 'pending' || activeTab !== 'qrcode') return;

    const poll = async () => {
      try {
        const resp = await fetch(`${API_BASE}/auth/qrcode/status/${qrToken}`);
        const data = await resp.json();
        if (data.code !== 200) return;

        const s = data.data;
        if (s.status === 'scanned') {
          setQrStatus('scanned');
          setScannerName(s.scannedBy || '');
        } else if (s.status === 'confirmed') {
          setQrStatus('confirmed');
          // 自动登录
          localStorage.setItem('admin_token', s.token);
          localStorage.setItem('admin_info', JSON.stringify(s.admin));
          onLogin?.(s.admin);
          message.success('扫码登录成功');
          setTimeout(() => navigate('/dashboard'), 500);
        } else if (s.status === 'expired') {
          setQrStatus('expired');
        }
      } catch {
        // 轮询静默失败
      }
    };

    pollRef.current = setInterval(poll, POLL_INTERVAL);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [qrToken, qrStatus, activeTab, navigate, onLogin]);

  // 切换到扫码标签时自动生成二维码
  useEffect(() => {
    if (activeTab === 'qrcode' && !qrToken) {
      generateQrCode();
    }
  }, [activeTab, qrToken, generateQrCode]);

  // 渲染密码登录表单
  const renderPasswordForm = () => (
    <>
      <Form onFinish={handleSubmit} size="large">
        <Form.Item name="username" rules={[{ required: true, message: '请输入用户名' }]}>
          <Input prefix={<UserOutlined />} placeholder="用户名" />
        </Form.Item>
        <Form.Item name="password" rules={[{ required: true, message: '请输入密码' }]}>
          <Input.Password prefix={<LockOutlined />} placeholder="密码" />
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" loading={loading} block>
            登录
          </Button>
        </Form.Item>
      </Form>

      {sysConfig.features.showLoginHint && (
        <Text type="secondary" style={{ textAlign: 'center', display: 'block', fontSize: 12 }}>
          默认账号: admin / admin123
        </Text>
      )}
    </>
  );

  // 渲染扫码登录
  const renderQrCode = () => {
    if (qrStatus === 'confirmed') {
      return (
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <Result status="success" title="登录成功，正在跳转..." />
        </div>
      );
    }

    if (qrStatus === 'expired') {
      return (
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <Result
            status="warning"
            title="二维码已过期"
            subTitle="请点击下方按钮重新生成"
          />
          <Button type="primary" icon={<ReloadOutlined />} onClick={generateQrCode}>
            刷新二维码
          </Button>
        </div>
      );
    }

    return (
      <div style={{ textAlign: 'center', padding: '16px 0' }}>
        {qrLoading ? (
          <Spin size="large" tip="生成二维码中..." />
        ) : qrToken ? (
          <>
            {/* 二维码图片 - 使用 qrserver API */}
            <div style={{
              width: 200, height: 200, margin: '0 auto 16px',
              border: '3px solid #f0f0f0', borderRadius: 8, overflow: 'hidden',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              position: 'relative',
            }}>
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(`https://${sysConfig.domain || 'www.lyjiayu.top'}/wxb/api/auth/qrcode/scan-page?token=${qrToken}`)}`}
                alt="扫码登录二维码"
                style={{ width: '100%', height: '100%' }}
              />
              {qrStatus === 'scanned' && (
                <div style={{
                  position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)',
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  justifyContent: 'center', borderRadius: 5,
                }}>
                  <div style={{ color: '#52c41a', fontSize: 40, marginBottom: 8 }}>✓</div>
                  <div style={{ color: '#fff', fontSize: 13 }}>已扫描</div>
                  {scannerName && <div style={{ color: '#ddd', fontSize: 12, marginTop: 4 }}>{scannerName}</div>}
                </div>
              )}
            </div>

            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
              {qrStatus === 'scanned'
                ? `「${scannerName}」已扫码，请在手机上确认登录`
                : '请使用小程序扫描二维码登录'}
            </Text>

            <Button
              type="link"
              size="small"
              icon={<ReloadOutlined />}
              onClick={generateQrCode}
              disabled={qrStatus === 'scanned'}
            >
              刷新二维码
            </Button>
          </>
        ) : (
          <Button type="primary" onClick={generateQrCode} loading={qrLoading}>
            获取二维码
          </Button>
        )}
      </div>
    );
  };

  const tabItems = [
    {
      key: 'password',
      label: <span><KeyOutlined /> 密码登录</span>,
      children: renderPasswordForm(),
    },
    {
      key: 'qrcode',
      label: <span><QrcodeOutlined /> 扫码登录</span>,
      children: renderQrCode(),
    },
  ];

  // SSO 加载中
  if (ssoLoading) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      }}>
        <Card style={{ width: 420, boxShadow: '0 8px 32px rgba(0,0,0,0.15)', textAlign: 'center' }}>
          <Spin size="large" tip="正在从主站同步登录..." style={{ padding: '40px 0' }}>
            <div style={{ height: 80 }} />
          </Spin>
        </Card>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    }}>
      <Card style={{ width: 420, boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <ToolOutlined style={{ fontSize: 48, color: '#1677ff' }} />
          <Title level={3} style={{ marginTop: 16, marginBottom: 4 }}>
            {sysConfig.companyShortName}{sysConfig.systemTitle}
          </Title>
          <Text type="secondary">{sysConfig.industryDesc}</Text>
        </div>

        {ssoError && (
          <div style={{
            background: '#fff2f0', border: '1px solid #ffccc7',
            borderRadius: 6, padding: '10px 14px', marginBottom: 16,
            fontSize: 13, color: '#cf1322',
          }}>
            <span>⚠️ {ssoError}</span>
          </div>
        )}

        <Tabs activeKey={activeTab} onChange={setActiveTab} centered items={tabItems} />

      </Card>
    </div>
  );
}

export default Login;
